// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { compose, withHandlers, branch } from 'recompose';
import classNames from 'classnames';
import { identity, last } from 'lodash/fp';

// Utils
import { customListDropTargetDecorator, listDropLocationFn } from '../../list/listDropTargetDecorator';
import { getListChildNewLocation } from '../../../common/elements/utils/elementLocationUtils';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';

// Constants
import { NATIVE_TYPES } from '../../utils/dnd/dragAndDropUtils';
import {
    DROP_TARGET_TYPES,
    ELEMENT_CREATION_SOURCES,
    ELEMENT_DND_TYPE,
} from '../../../common/elements/elementConstants';

// Styles
import './ColumnBottomDropArea.scss';

const dropTargetConfig = {
    drop: listDropLocationFn,
    canDrop: (props, monitor) => {
        const { listCanDropFn } = props;
        return listCanDropFn(props, monitor);
    },
    acceptDropTypes: NATIVE_TYPES,
    hover: (props, monitor) => {
        if (!monitor || !monitor.canDrop()) return;

        const { isCollapsed } = props;
        monitor.getItem().index = isCollapsed ? 0 : props.listSize;
    },
};

const showHover = (monitor, props) => {
    const { childElementIds, isCollapsed } = props;

    // Don't capture the drop if the dragged element is from the column and there's
    // only one element in the list
    const item = monitor.getItem();
    const itemType = monitor.getItemType();

    // For files show the drop target if the column is collapsed or there's children
    if (itemType !== ELEMENT_DND_TYPE) return isCollapsed || !!childElementIds?.length;

    if (!item || (!isCollapsed && (!childElementIds || !childElementIds.length))) return false;

    const { element } = item;

    if (!element) return false;

    const lastElementId = last(childElementIds);
    const elementId = getElementId(element);

    return elementId !== lastElementId;
};

const listDropCollect = (connector, monitor, props) => ({
    connectDropTarget: connector.dropTarget(),
    isHovered: monitor.canDrop() && monitor.isOver({ shallow: true }) && showHover(monitor, props),
});

const enhance = branch(
    ({ isPresentational, isEditable }) => isPresentational || !isEditable,
    identity,
    compose(
        withHandlers({
            onDoubleClick:
                ({ createShortcutCard, listSize, listId, isCollapsed }) =>
                () => {
                    // Don't add a new card in the column when double clipping if it's collapsed
                    if (isCollapsed) return;

                    const location = getListChildNewLocation({ listId, index: listSize });

                    createShortcutCard({
                        location,
                        currentBoardId: listId,
                        creationSource: ELEMENT_CREATION_SOURCES.DOUBLE_CLICK,
                    });
                },
        }),
        customListDropTargetDecorator(dropTargetConfig, DROP_TARGET_TYPES.LIST, listDropCollect),
    ),
);

/**
 * Note: Using a class component so ReactDnD can find the DOM node that this component is dropped on.
 */
class ColumnBottomDropArea extends React.Component {
    render() {
        const { connectDropTarget, isHovered, listSize, isCollapsed, onDoubleClick, isEditable } = this.props;

        if (!isEditable || (!isCollapsed && (!listSize || listSize < 1))) return null;

        const classes = classNames('ColumnBottomDropArea', { 'collapsed-hovered': isHovered && isCollapsed });

        const wrapper = connectDropTarget || identity;
        return wrapper(
            <div className={classes} onDoubleClick={onDoubleClick}>
                {isHovered && !isCollapsed && <div className="hovered-placeholder" />}
            </div>,
        );
    }
}

ColumnBottomDropArea.propTypes = {
    isPresentational: PropTypes.bool,
    listId: PropTypes.string,
    listCanDropFn: PropTypes.func,
    listSize: PropTypes.number,

    onDoubleClick: PropTypes.func,

    connectDropTarget: PropTypes.func,
    isEditable: PropTypes.bool,
    isHovered: PropTypes.bool,
    isCollapsed: PropTypes.bool,
};

export default enhance(ColumnBottomDropArea);
